import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { Table, Button, Modal, Input, Badge, Field } from '@itds/elements';

const ApiTokens = ({ api }) => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newKeyDisplay, setNewKeyDisplay] = useState(null);
  const [copyStatus, setCopyStatus] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);

  const [formData, setFormData] = useState({
    id: null,
    service_name: '',
    description: ''
  });

  const loadTokens = () => {
    setLoading(true);
    api.loadExternalServices()
      .then((res) => {
        setTokens(res.data);
      })
      .catch((err) => console.error('Error loading API Tokens', err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadTokens();
  }, []);

  const handleOpenModal = (token = null) => {
    setNewKeyDisplay(null);
    setCopyStatus(null);
    if (token && token.id) {
      setFormData({
        id: token.id,
        service_name: token.service_name || token.serviceName || '',
        description: token.description || ''
      });
    } else {
      setFormData({ id: null, service_name: '', description: '' });
    }
    setIsModalOpen(true);
  };

  const handleSave = () => {
    const payload = {
      description: formData.description,
      service_name: formData.service_name,
      serviceName: formData.service_name
    };

    if (formData.id) {
      api.updateExternalService(formData.id, payload).then(() => {
        setIsModalOpen(false);
        loadTokens();
      });
    } else {
      api.createExternalService(payload).then((res) => {
        setNewKeyDisplay(res.data.api_key || res.data.apiKey);
        loadTokens();
        // Do not close modal immediately so they can copy the key
      });
    }
  };

  const handleRevoke = (id) => {
    setConfirmDialog({
      title: 'Revoke ApiToken',
      message: 'Are you sure you want to revoke this key?',
      confirmLabel: 'Revoke',
      confirmColor: 'RED',
      onConfirm: () => {
        setConfirmDialog(null);
        api.revokeExternalService(id).then(loadTokens);
      }
    });
  };

  const handleRegenerate = (id) => {
    setConfirmDialog({
      title: 'Regenerate ApiToken',
      message: 'Are you sure you want to regenerate this key? The old key will be invalidated instantly.',
      confirmLabel: 'Regenerate',
      confirmColor: 'PRIMARY',
      onConfirm: () => {
        setConfirmDialog(null);
        api.regenerateExternalServiceKey(id).then((res) => {
          setNewKeyDisplay(res.data.api_key || res.data.apiKey);
          setCopyStatus(null);
          setFormData({ id });
          setIsModalOpen(true);
          loadTokens();
        });
      }
    });
  };

  const handleDelete = (id) => {
    setConfirmDialog({
      title: 'Delete ApiToken',
      message: 'Are you sure you want to permanently delete this integration?',
      confirmLabel: 'Delete',
      confirmColor: 'RED',
      onConfirm: () => {
        setConfirmDialog(null);
        api.deleteExternalService(id).then(loadTokens);
      }
    });
  };

  const copyToClipboard = () => {
    if (newKeyDisplay) {
      navigator.clipboard.writeText(newKeyDisplay);
      setCopyStatus('API Key copied to clipboard!');
    }
  };

  const columns = [
    { label: 'ID', id: 'id' },
    { label: 'Service Name', id: 'service_name' },
    { label: 'Description', id: 'description' },
    {
      label: 'Status',
      render: (row) => {
        const isActive = row.is_active ?? row.isActive;

        return (
          <Badge color={isActive ? 'GREEN' : 'RED'}>
            {isActive ? 'Active' : 'Revoked'}
          </Badge>
        );
      }
    },
    {
      label: 'Created',
      render: (row) => {
        const date = row.created_date || row.createdDate;

        return date ? format(new Date(date), 'yyyy-MM-dd') : 'N/A';
      }
    },
    {
      label: 'Last Used',
      render: (row) => {
        const date = row.last_used_date || row.lastUsedDate;

        return date ? format(new Date(date), 'yyyy-MM-dd') : 'N/A';
      }
    },
    {
      label: 'Actions',
      render: (row) => {
        const isActive = row.is_active ?? row.isActive;

        return (
          <div style={{ display: 'flex', gap: '10px' }}>
            <Button onClick={() => handleOpenModal(row)} secondary={true} small={true}>Edit</Button>
            {isActive ? (
              <React.Fragment>
                <Button color='RED' onClick={() => handleRevoke(row.id)} small={true}>Revoke</Button>
                <Button onClick={() => handleRegenerate(row.id)} secondary={true} small={true}>Regen</Button>
              </React.Fragment>
            ) : null}
            <Button color='RED' onClick={() => handleDelete(row.id)} small={true}>Delete</Button>
          </div>
        );
      }
    }
  ];

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
        <h2>Provisioned ApiTokens</h2>
        <Button onClick={() => handleOpenModal()}>Create New ApiToken</Button>
      </div>

      <Table
        columns={columns}
        data={tokens}
        loading={loading}
      />

      {isModalOpen && (
        <Modal maxWidth={500} onClose={() => setIsModalOpen(false)} title={formData.id ? 'Edit ApiToken' : 'Create New ApiToken'}>
          {newKeyDisplay ? (
            <div style={{ padding: '20px' }}>
              <div style={{ padding: '15px', background: '#ffebee', border: '1px solid #ffcdd2', borderRadius: '5px', marginBottom: '20px' }}>
                <h3 style={{ color: '#c62828', marginTop: 0, fontSize: '14px', fontWeight: 'bold' }}>IMPORTANT!</h3>
                <p style={{ color: '#b71c1c', fontSize: '14px', margin: 0, lineHeight: '1.5' }}>Here is your actual ApiKey. Please copy it immediately. It is irreversibly hashed in the database and this is the only time it will ever be displayed.</p>
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                <Field style={{ flex: 1 }}>
                  <Input readOnly={true} value={newKeyDisplay} />
                </Field>
                <Button onClick={copyToClipboard} style={{ marginTop: '25px' }}>Copy</Button>
              </div>
              {copyStatus ? (
                <div style={{ color: '#2e7d32', fontSize: '13px', marginTop: '12px' }}>
                  {copyStatus}
                </div>
              ) : null}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', padding: '20px' }}>
              <Field label='Service Name'>
                <Input
                  onChange={(e) => setFormData({ ...formData, service_name: e.target.value })}
                  value={formData.service_name}
                />
              </Field>
              <Field label='Description'>
                <Input
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  value={formData.description}
                />
              </Field>
              <Button onClick={handleSave} style={{ alignSelf: 'flex-end', marginTop: '10px' }}>
                {formData.id ? 'Save Changes' : 'Generate ApiKey'}
              </Button>
            </div>
          )}
        </Modal>
      )}

      {confirmDialog ? (
        <Modal
          buttons={[
            {
              children: 'Cancel',
              onClick: () => setConfirmDialog(null),
              outline: true
            },
            {
              children: confirmDialog.confirmLabel,
              color: confirmDialog.confirmColor,
              onClick: confirmDialog.onConfirm
            }
          ]}
          maxWidth={450}
          onClose={() => setConfirmDialog(null)}
          title={confirmDialog.title}
        >
          <div style={{ padding: '20px', lineHeight: '1.5em' }}>
            {confirmDialog.message}
          </div>
        </Modal>
      ) : null}
    </div>
  );
};

ApiTokens.propTypes = {
  api: PropTypes.object.isRequired
};

export default ApiTokens;
